import { Menu, Box, MenuItem, PopoverOrigin, Stack } from '@mui/material';
import { ChevronDown, Cloud, Devops } from '@sweep-io/sweep-design/dist/icons';
import { Button, Tooltip, Typography } from '@sweep-io/sweep-design';
import usePopover from '../../../hooks/usePopover';
import SplitButton from './SplitButton';
import { useCallback, useState } from 'react';
import { OrgDot } from '../OrgDot';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { AutomationDeployAs } from '../../../types/enums/AutomationDeployAs';
import {
  InvalidValidationReasons,
  invalidValidationReasonsMap,
} from '../../Automations/automation-form-validation/deployToFlowHelper';

const menuItemSx = { display: 'flex', alignItems: 'center', gap: 1 };

const deployTextMap = {
  [AutomationDeployAs.AUTOMATION]: 'Deploy to',
  [AutomationDeployAs.FLOW]: 'Deploy as Salesforce flow',
  ['REQUEST_TO_DEPLOY']: 'Request to deploy to',
};

const dropdownLocationMap: {
  [key: string]: {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
  };
} = {
  bottom: {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'right',
    },
  },
  top: {
    anchorOrigin: {
      vertical: -10,
      horizontal: 'right',
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    },
  },
};

export type DeployAsFlowOption = {
  display: true;
  disabled?: boolean;
};

export interface DeployButtonProps {
  defaultCreationEnvironmentName?: string;
  prodEnvironmentName?: string;
  defaultIsProd: boolean;
  onDeployDefaultClick?: (deployAs: AutomationDeployAs) => Promise<void>;
  onDeployBothClick?: (deployAs: AutomationDeployAs) => Promise<void>;
  onSaveClick: () => Promise<void>;
  disabled?: boolean;
  onShowDiff?: () => void;
  defaultDeployType: AutomationDeployAs;
  deployAsFlowOption?: DeployAsFlowOption;
  dropdownPosition?: 'bottom' | 'top';
  isRequestToDeploy?: boolean;
  invalidValidationReasons?: InvalidValidationReasons[];
}

const DeployButton = ({
  defaultCreationEnvironmentName,
  prodEnvironmentName,
  defaultIsProd,
  onDeployDefaultClick,
  onDeployBothClick,
  onSaveClick,
  disabled,
  onShowDiff,
  defaultDeployType,
  deployAsFlowOption,
  dropdownPosition = 'top',
  isRequestToDeploy,
  invalidValidationReasons,
}: DeployButtonProps) => {
  const { deployAutomationAsFlow: ftDeployAutomationAsFlow } = useFeatureToggle();
  const { isPopoverOpen, closePopover, openPopover, anchorElement, anchorRef } = usePopover();
  const [isLoading, setIsLoading] = useState(false);

  const deployText = deployTextMap[isRequestToDeploy ? 'REQUEST_TO_DEPLOY' : defaultDeployType];

  const callAsyncWithLoading = async (fn: () => Promise<void>) => {
    setIsLoading(true);
    await fn();
    setIsLoading(false);
  };

  const showDeployAsFlowOption = Boolean(
    deployAsFlowOption && ftDeployAutomationAsFlow && !isRequestToDeploy,
  );

  const isFlow = defaultDeployType === AutomationDeployAs.FLOW;
  const showDeployToSandboxPlusProd = Boolean(
    !defaultIsProd && !isRequestToDeploy && onDeployBothClick && prodEnvironmentName && !isFlow,
  );
  const showDiffItem = Boolean(onShowDiff);
  const totalMenuEntries =
    Number(showDeployToSandboxPlusProd) + Number(showDiffItem) + Number(showDeployAsFlowOption);
  const shouldRenderMenu = totalMenuEntries > 0;

  let buttonText = deployText;
  if (!isFlow) {
    buttonText = `${deployText} ${defaultCreationEnvironmentName}`;
  }
  const dropdownLocation = dropdownLocationMap[dropdownPosition];

  const _onDeployDefaultClick = useCallback(
    (deployAs: AutomationDeployAs = defaultDeployType) => {
      onDeployDefaultClick && callAsyncWithLoading(() => onDeployDefaultClick(deployAs));
      closePopover();
    },
    [onDeployDefaultClick, defaultDeployType, closePopover],
  );

  const _onDeployBothClick = useCallback(() => {
    onDeployBothClick && callAsyncWithLoading(() => onDeployBothClick(defaultDeployType));
    closePopover();
  }, [onDeployBothClick, defaultDeployType, closePopover]);

  const renderDeployAsFlowInvalidTooltip = (children: React.ReactNode) => {
    return (
      <Tooltip
        placement="top-end"
        theme="light"
        title={
          <Box display="flex" flexDirection="column" gap={1} padding={1}>
            <Stack>
              <Typography variant="body">
                This automation can’t be deployed as a Flow because it includes Sweep-specific
                functionality like assignment, Slack/Teams actions, or scheduled jobs. Remove the
                following:
              </Typography>
            </Stack>
            <Stack>
              {invalidValidationReasons?.map((reason) => (
                <Typography variant="body" key={reason}>
                  • {invalidValidationReasonsMap[reason]}
                </Typography>
              ))}
            </Stack>
            <Stack>
              <Button variant="flat-blue" underlined={true} size="small">
                Learn more about when to use each →
              </Button>
            </Stack>
          </Box>
        }
      >
        <span>{children}</span>
      </Tooltip>
    );
  };

  const maybeRenderDeployAsFlowOption = () => {
    if (!showDeployAsFlowOption) {
      return null;
    }

    const option = (
      <MenuItem
        sx={menuItemSx}
        onClick={() => _onDeployDefaultClick(AutomationDeployAs.FLOW)}
        disabled={deployAsFlowOption?.disabled}
      >
        <Typography variant="body">{deployTextMap[AutomationDeployAs.FLOW]}</Typography>
      </MenuItem>
    );
    if (deployAsFlowOption?.disabled) {
      return renderDeployAsFlowInvalidTooltip(option);
    }

    return option;
  };

  const renderMenu = () => {
    return (
      <Menu
        anchorEl={anchorElement}
        open={isPopoverOpen}
        onClose={() => closePopover()}
        {...dropdownLocation}
      >
        {showDeployToSandboxPlusProd && (
          <MenuItem sx={menuItemSx} onClick={_onDeployBothClick}>
            <Cloud size="medium" />
            <Box display="flex" gap={0.5} alignItems="center">
              <Typography variant="body">
                {deployText} {defaultCreationEnvironmentName} + {prodEnvironmentName}
              </Typography>
              <OrgDot />
            </Box>
          </MenuItem>
        )}
        {showDiffItem && (
          <MenuItem
            sx={menuItemSx}
            onClick={() => {
              onShowDiff?.();
              closePopover();
            }}
          >
            <Devops />
            <Typography variant="body">Review Changes</Typography>
          </MenuItem>
        )}
        {maybeRenderDeployAsFlowOption()}
      </Menu>
    );
  };

  const renderSimpleDeployButton = () => {
    const button = (
      <Button
        size="medium"
        loading={isLoading}
        disabled={!defaultCreationEnvironmentName || disabled}
        onClick={() => _onDeployDefaultClick()}
      >
        {buttonText}
      </Button>
    );
    if (!isFlow || !Boolean(invalidValidationReasons?.length)) {
      return button;
    }

    return renderDeployAsFlowInvalidTooltip(button);
  };

  const renderSplitDeployButton = () => {
    const button = (
      <SplitButton
        leftText={buttonText}
        leftButtonProps={{
          onClick: () => _onDeployDefaultClick(),
          disabled: !defaultCreationEnvironmentName || disabled,
        }}
        rightButtonProps={{
          onClick: openPopover,
          pressed: isPopoverOpen,
          disabled: !defaultCreationEnvironmentName || disabled,
        }}
        rightIcon={<ChevronDown size="medium" />}
        isLoading={isLoading}
      />
    );
    if (!isFlow || !Boolean(invalidValidationReasons?.length)) {
      return button;
    }

    return renderDeployAsFlowInvalidTooltip(button);
  };
  return (
    <Box display="flex" gap="12px">
      <Button
        size="medium"
        variant="system"
        onClick={async () => {
          callAsyncWithLoading(onSaveClick);
          closePopover();
        }}
        loading={isLoading}
        disabled={!defaultCreationEnvironmentName || disabled}
      >
        Save draft
      </Button>
      {shouldRenderMenu && (
        <>
          <Box display="inline-flex" ref={anchorRef}>
            {renderSplitDeployButton()}
          </Box>
          {renderMenu()}
        </>
      )}

      {!shouldRenderMenu && renderSimpleDeployButton()}
    </Box>
  );
};

export default DeployButton;
